// Device-local operator settings.
//
// These are per-workstation runtime preferences (stored via the platform
// `storage` util — Electron userData JSON / Capacitor prefs / web localStorage),
// surfaced on the admin-only Settings page and edited through SettingsContext.
//
// This replaces the old compile-time `QUEUE_ENABLED` flag in features.ts: the
// customer queue can now be turned on/off at runtime without rebuilding. Add
// future toggles by extending `AppSettings`, `SETTINGS_DEFAULTS`, and
// `SETTINGS_SCHEMA` — the Settings page renders every entry automatically.

export interface AppSettings {
  /**
   * Customer queue mode. When `true` the dashboard shows the Next-Up widget and
   * the second screen shows the customer-facing queue display. When `false` the
   * dashboard shows the legacy per-experience instruction buttons and the second
   * screen shows the instruction image. Both code paths stay wired up either way.
   *
   * Deliberately per-device and default false: venues without a queue keep the
   * instruction buttons, and the queue venue switches it on per workstation.
   * Deriving the default from booking.queue_config is a follow-up. The
   * staging-only test tools (MOCK_LAUNCH, fake PC tiles) are NOT settings:
   * they live in localStorage and are edited from the "Staging tools" block on
   * the Settings page (see utils/env.ts, DEV_TOOLS_ENABLED).
   */
  queueEnabled: boolean;

  /**
   * Whether a PC freeing up opens "Volgende bezoeker" even though another PC
   * was already standing idle. On a quiet moment the operator can see the free
   * tiles and start people themselves, so the pop-up is just an interruption;
   * when the room WAS full, a PC freeing up is the event that matters and the
   * pop-up is the whole point. Off = only prompt when the room was full.
   * Default on, which is how the queue behaved before this switch existed.
   */
  queuePromptWhenPcsFree: boolean;

  /**
   * Audio alert for the end-of-run notifications — both the agent's
   * `last_level_warning` ("bijna klaar", raised on the final scene) and the
   * stronger `visitor_done` ("nu klaar", raised when the build reports the
   * visitor has actually finished). When off, this device stays silent for both.
   * Volume is a 0–100 percentage; see NOTIFICATION_MAX_GAIN for the mapping.
   */
  nearlyDoneSoundEnabled: boolean;
  nearlyDoneVolume: number;

  /**
   * Blink the PC tile in alarm colours while `visitor_done` is showing, i.e.
   * from the moment the visitor is reported finished until the experience is
   * closed. Off by default because it is deliberately hard to ignore; the
   * bigger red banner is shown either way.
   */
  visitorDoneBlinkEnabled: boolean;

  /**
   * Audio alert for "something went wrong" notifications (agent error
   * notifications — headset issues, crashes, etc.). Independent enable + volume
   * from the almost-finished alert so the two can be tuned separately.
   */
  errorSoundEnabled: boolean;
  errorVolume: number;
}

export const SETTINGS_DEFAULTS: AppSettings = {
  queueEnabled: false,
  queuePromptWhenPcsFree: true,
  nearlyDoneSoundEnabled: true,
  // 50% maps to the historical fixed gain (0.3) — see NOTIFICATION_MAX_GAIN.
  nearlyDoneVolume: 50,
  visitorDoneBlinkEnabled: false,
  errorSoundEnabled: true,
  errorVolume: 50,
};

// The Web Audio gain applied at 100% volume. A 0–100 setting maps linearly onto
// 0..NOTIFICATION_MAX_GAIN, so the 50% default reproduces the previous hardcoded
// 0.3 gain while leaving headroom to make alerts louder.
export const NOTIFICATION_MAX_GAIN = 0.6;

// Keys are persisted individually as `settings.<key>` so adding a new setting
// never disturbs values already stored on a device.
export const SETTINGS_STORAGE_PREFIX = 'settings.';

// Drives the Settings page UI. Every setting listed here gets a row.
export type SettingDef =
  | {
      key: keyof AppSettings;
      label: string;
      description: string;
      type: 'boolean';
      // Optional: row is greyed out / disabled while this boolean setting is
      // false (a rule that only means anything once its parent is on).
      enabledBy?: keyof AppSettings;
    }
  | {
      key: keyof AppSettings;
      label: string;
      description: string;
      type: 'volume';
      // Row is greyed out / disabled while this boolean setting is false.
      enabledBy: keyof AppSettings;
    };

export const SETTINGS_SCHEMA: SettingDef[] = [
  {
    key: 'queueEnabled',
    label: 'Customer queue',
    description:
      'Show the Next-Up widget on the dashboard and the customer-facing queue on the second screen. ' +
      'When off, the dashboard shows the per-experience instruction buttons and the second screen ' +
      'shows the instruction image.',
    type: 'boolean',
  },
  {
    key: 'queuePromptWhenPcsFree',
    label: 'Pop-up ook als er al een PC vrij was',
    description:
      'Toon "Volgende bezoeker" telkens als een PC vrijkomt. Zet uit om de pop-up alleen te ' +
      'krijgen wanneer alle PC\'s bezet waren: op rustige momenten zie je de vrije tegels toch al ' +
      'staan en onderbreekt de pop-up alleen maar.',
    type: 'boolean',
    enabledBy: 'queueEnabled',
  },
  {
    key: 'nearlyDoneSoundEnabled',
    label: 'Sound: visitor finishing',
    description:
      'Play a chime on this device when a visitor is nearly done with their experience, ' +
      'and again when they are reported finished.',
    type: 'boolean',
  },
  {
    key: 'nearlyDoneVolume',
    label: 'Volume: visitor finishing',
    description: 'Loudness of the visitor-finishing chimes on this device.',
    type: 'volume',
    enabledBy: 'nearlyDoneSoundEnabled',
  },
  {
    key: 'visitorDoneBlinkEnabled',
    label: 'Blink tile when visitor is finished',
    description:
      'Flash the PC tile in alarm colours from the moment the visitor is reported finished ' +
      'until the experience is closed, so it cannot be missed across the room.',
    type: 'boolean',
  },
  {
    key: 'errorSoundEnabled',
    label: 'Sound: something went wrong',
    description:
      'Play an alert on this device when a PC reports an error (headset issue, crash, and similar).',
    type: 'boolean',
  },
  {
    key: 'errorVolume',
    label: 'Volume: something went wrong',
    description: 'Loudness of the something-went-wrong alert on this device.',
    type: 'volume',
    enabledBy: 'errorSoundEnabled',
  },
];
